/**
 * Mock data for each phase of the Bundle Upload flow.
 * Use with BundleUploadStepPreview to visualize all states.
 *
 * Phases map to:
 *   UploadPhase   → Steps 1–3  (file selected → upload → classify)
 *   PollingPhase  → Steps 4a–4b (processing / polling task status)
 *   ResultPhase   → Steps 5–10  (fetch results → save → review / error)
 */

export const createMockFile = (
  name = "bundled_input.pdf",
  size = 15_630_000
): File =>
  new File([""], name, { type: "application/pdf", lastModified: Date.now() });

// ---------------------------------------------------------------------------
// UploadPhase
// ---------------------------------------------------------------------------

export const MOCK_STEP_PRE_UPLOAD = {
  description: "UploadPhase — File selected, ready to upload",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    uploadProgress: undefined,
    onClear: () => console.log("clear"),
  },
  button: { text: "Continue to Processing", disabled: false },
} as const;

export const MOCK_STEP_UPLOADING_START = {
  description: "UploadPhase — Upload started (indeterminate)",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: undefined,
  },
  progress: { indeterminate: true, label: "Uploading File" },
} as const;

export const MOCK_STEP_UPLOADING_PROGRESS = {
  description: "UploadPhase — Upload in progress (45%)",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: undefined,
  },
  progress: { indeterminate: false, value: 45, label: "Uploading File (45%)" },
} as const;

export const MOCK_STEP_CLASSIFYING = {
  description: "UploadPhase — Preparing classification",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: undefined,
  },
  progress: { indeterminate: true, label: "Preparing Classification" },
} as const;

// ---------------------------------------------------------------------------
// PollingPhase
// ---------------------------------------------------------------------------

export const MOCK_STEP_PROCESSING_START = {
  description: "PollingPhase — Processing started (0%)",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: 0,
  },
  statusText: "Splitting bundle",
  pollingMessage:
    "This may take a few minutes depending on number and size of documents.",
} as const;

export const MOCK_STEP_PROCESSING_PROGRESS = {
  description: "PollingPhase — Processing in progress (60%)",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: 60,
  },
  statusText: "Classifying documents",
  pollingMessage:
    "This may take a few minutes depending on number and size of documents.",
} as const;

// ---------------------------------------------------------------------------
// ResultPhase
// ---------------------------------------------------------------------------

export const MOCK_STEP_FETCHING_RESULTS = {
  description: "ResultPhase — Fetching results after processing",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: undefined,
  },
  progress: { indeterminate: true, label: "Loading Results" },
} as const;

export const MOCK_STEP_SAVING_FILES = {
  description: "ResultPhase — Saving classified files",
  filePreview: {
    isSuccess: false,
    isFailed: false,
    isLoading: true,
    uploadProgress: undefined,
  },
  progress: { indeterminate: true, label: "Saving Files" },
} as const;

export const MOCK_STEP_SAVE_SUCCESS = {
  description: "ResultPhase — Save complete (all classified)",
  filePreview: {
    isSuccess: true,
    isFailed: false,
    uploadProgress: undefined,
    onClear: () => console.log("dismiss"),
  },
  alert: { variant: "success" as const, message: "Files saved successfully" },
} as const;

export const MOCK_STEP_SAVE_NEEDS_REVIEW = {
  description: "ResultPhase — Save complete (5 docs need classification)",
  filePreview: {
    isSuccess: true,
    isFailed: false,
    uploadProgress: undefined,
    onClear: () => console.log("dismiss"),
  },
  alert: {
    variant: "warning" as const,
    message:
      "We have mapped most of your documents. But 5 documents need classification. Please review before continuing.",
  },
  button: { text: "Review Now", disabled: false },
} as const;

export const MOCK_STEP_SAVE_ERROR = {
  description: "ResultPhase — Save failed",
  filePreview: {
    isSuccess: false,
    isFailed: true,
    uploadProgress: undefined,
  },
  alert: {
    variant: "destructive" as const,
    message: "Failed to save classified files. Please try again.",
  },
  button: { text: "Retry Save", disabled: false },
} as const;

export const MOCK_STEP_UPLOAD_ERROR = {
  description: "UploadPhase — Upload or classification failed",
  filePreview: {
    isSuccess: false,
    isFailed: true,
    uploadProgress: undefined,
    onClear: () => console.log("clear"),
  },
  alert: {
    variant: "destructive" as const,
    message: "File upload failed. Please check your connection and try again.",
  },
  button: { text: "Continue to Processing", disabled: false },
} as const;

// All steps in phase order
export const ALL_MOCK_STEPS = [
  MOCK_STEP_PRE_UPLOAD,
  MOCK_STEP_UPLOADING_START,
  MOCK_STEP_UPLOADING_PROGRESS,
  MOCK_STEP_CLASSIFYING,
  MOCK_STEP_PROCESSING_START,
  MOCK_STEP_PROCESSING_PROGRESS,
  MOCK_STEP_FETCHING_RESULTS,
  MOCK_STEP_SAVING_FILES,
  MOCK_STEP_SAVE_SUCCESS,
  MOCK_STEP_SAVE_NEEDS_REVIEW,
  MOCK_STEP_SAVE_ERROR,
  MOCK_STEP_UPLOAD_ERROR,
] as const;
